import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ApiService } from '../service/api.service';
import { DatePipe } from '@angular/common';
import { SortDescriptor, State, process } from '@progress/kendo-data-query';
import {
  PageChangeEvent,
  GridComponent
} from '@progress/kendo-angular-grid';
import { LoaderService } from '../loader/loader.service';
import { GridSettingsService } from '../service/grid-settings.service';
import { toGridData, toServerState } from '../common/grid-grouping';
import { toLocalState } from '../common/grid-filter';

// Fields the search box matches in the "Free Subscription" list, which is searched here rather than by the API
const SEARCH_FIELDS = [
  'TransactionNumber',
  'Date',
  'CreditCardNumber',
  'TotalAmount',
  'TransactionStatusStr',
  'UserName',
  'TransactionTypeName',
  'CompanyAddress',
  'CompanyName',
  'SubscriptionPlanId',
];

@Component({
    selector: 'app-transaction',
    templateUrl: './transaction.component.html',
    styleUrls: ['./transaction.component.css'],
    providers: [DatePipe],
    standalone: false
})
export class TransactionComponent implements OnInit, AfterViewInit {
  @ViewChild("grid", { static: true }) gridKendo: GridComponent;
  public gridHeight: number;
  public gridData: any;
  transactionData: any = [];
  searchString: any = '';
  state: State = {
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
    group: [],
  };
  public sort: SortDescriptor[] = [
  ];
  total: number = 0;
  kendoLoading = false;
  public pageableWithAll: any = {
    buttonCount: 5,
    pageSizes: [5, 10, 20, 50, 100, { text: 'All', value: 'all' }]
  };
  isDisableToggle: boolean = false;
  IsnotZero: boolean = true;
  selectedStatus: string = 'All';
  statusOptions: { label: string; value: string }[] = [
    { label: 'All', value: 'All' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Unpaid', value: 'Unpaid' },
  ];
  TableName = "Transactions";
  constructor(private readonly api: ApiService, public datePipe: DatePipe, private readonly LoaderService: LoaderService,
    private readonly gridSettings: GridSettingsService) { }

  ngOnInit(): void {
    this.GetTransactionBigAmount();
    this.setGridHeight();
  }
  public dataStateChange(state: any): void {
    this.state = state;
    this.reloadTransactions();
  }
  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.state = { ...this.state, sort };
    this.reloadTransactions();
  }
  public pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.reloadTransactions();
  }

  // Paging, sorting and grouping stay on whichever list the toggle is showing
  private reloadTransactions(): void {
    if (this.IsnotZero) {
      this.GetTransactionBigAmount();
    } else {
      this.loadData();
    }
  }

  private loadData(): void {
    // Zero-amount transactions arrive as one full list and are searched, filtered and paged here; the others
    // are by the API
    this.gridData = this.IsnotZero
      ? toGridData(this.transactionData, this.total, this.state)
      : process(this.searchedTransactions(), toLocalState(this.state));
  }

  private searchedTransactions(): any[] {
    const search = this.searchString.trim().toLowerCase();
    if (!search) {
      return this.transactionData;
    }
    return this.transactionData.filter((transaction: any) =>
      SEARCH_FIELDS.some(field => transaction[field]?.toString().toLowerCase().includes(search)));
  }

  filterData(event: any) {
    // The column filters stay as they are; the search text is matched by the API, or here for free subscriptions
    this.searchString = event;
    this.state.skip = 0;
    this.reloadTransactions();
  }


  onStatusChange(value: string) {
    this.selectedStatus = value;
    this.state.skip = 0;
    this.GetTransactionBigAmount();
  }

  GetTransactionBigAmount() {
    this.LoaderService.show();
    this.api.GetTransactionBigAmount(toServerState(this.state), JSON.stringify(this.searchString.trim()), this.selectedStatus)
      .subscribe((res) => {
        const dataArray = res.Data?.Data || [];

        dataArray.forEach((e: any) => {
          e.Date = this.datePipe.transform(new Date(e.Date), 'dd-MM-yyyy hh:mm:ss a');

          e.SubscriptionPlanId = e.PlanName || '';

          // TotalAmount stays a number so the column's numeric filter can compare it; the cell adds the symbol
          e.CurrencySymbol = e.TotalAmount && e.IFOUR_CompanyMasterDto?.CurrencyObj
            ? e.IFOUR_CompanyMasterDto.TransactionCurrencyObj?.CurrencySymbol ?? ''
            : '';

          if (e.IFOUR_CompanyMasterDto?.CompanyName) {
            e.CompanyName = e.IFOUR_CompanyMasterDto.CompanyName;
          }

          if (e.IFOUR_CompanyMasterDto?.CompanyAddress) {
            e.CompanyAddress = e.IFOUR_CompanyMasterDto.CompanyAddress;
          }

          if (e.IFOUR_CompanyMasterDto?.IPAddress) {
            e.IPAddress = e.IFOUR_CompanyMasterDto.IPAddress;
          }

          e.TransactionCurrencyName = e.IFOUR_CompanyMasterDto?.TransactionCurrencyObj.CurrencyCode ?? '';
        });

        this.transactionData = dataArray;
        this.total = res.Data?.Total || 0;

        this.loadData();
        this.LoaderService.hide();
      });
  }


  GetTransactionZeroAmount() {
    this.LoaderService.show();

    this.api.GetTransactionZeroAmount()
      .subscribe((res: any) => {
        // 1. pull out the inner array (or default to empty)
        const dataArray: any[] = Array.isArray(res.Data?.Data)
          ? res.Data.Data
          : [];

        // 2. map & format each record
        dataArray.forEach((e: any) => {
          // date → dd-MM-yyyy
          const rawDate = e.Date.split('T')[0];
          e.Date = this.datePipe.transform(new Date(rawDate), 'dd-MM-yyyy');
          e.SubscriptionPlanId = e.PlanName || '';
          e.CurrencySymbol = e.TotalAmount != null && e.IFOUR_CompanyMasterDto?.CurrencyObj
            ? e.IFOUR_CompanyMasterDto.TransactionCurrencyObj?.CurrencySymbol ?? ''
            : '';

          e.CompanyName = e.IFOUR_CompanyMasterDto?.CompanyName || '';
          e.CompanyAddress = e.IFOUR_CompanyMasterDto?.CompanyAddress || '';
          e.IPAddress = e.IFOUR_CompanyMasterDto?.IPAddress || '';
          e.TransactionCurrencyName = e.IFOUR_CompanyMasterDto?.TransactionCurrencyObj.CurrencyCode ?? '';
        });
        this.transactionData = dataArray;

        // 4. refresh
        this.loadData();
        this.LoaderService.hide();

      }, err => {
        console.error('Error fetching zero-amount transactions', err);
      });
  }


  changeToggleVisit(event: any) {
    this.IsnotZero = event.value;
    this.state.skip = 0;
    if (this.IsnotZero) {
      this.GetTransactionBigAmount();

    }
    else {
      this.GetTransactionZeroAmount();
    }
  }


  clearList() {
    this.searchString = '';
    this.state.skip = 0;
    this.reloadTransactions();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setGridHeight();
  }

  setGridHeight(): void {
    const headerHeight = 70; // Adjust according to your header and other elements
    const footerHeight = 61;
    const otherElementsHeight = 125;
    this.gridHeight = window.innerHeight - headerHeight - footerHeight - otherElementsHeight;
  }

  onColumnVisibilityChange(columnEvent: GridComponent): void {
    this.gridSettings.saveGridSettings(columnEvent, this.TableName).subscribe();
  }

  ngAfterViewInit(): void {
    this.gridSettings.applyGridSettings(this.api, this.TableName, this.gridKendo);
  }
}
